import {
  type Campaign,
  type CampaignTrackHandle,
  type CampaignTrackPost,
  type UpdateCampaignRequest,
  buildCampaignTrackPostItem,
  classifyCreatorSource,
  isCampaignSupportedPlatform,
  resolveCampaignStartDateUpdate,
  splitTrackingTerms,
  toCampaignPlatform,
} from '@/entities/campaign';

import type { CampaignEditorSubmitPayload } from '../../../campaign-editor/campaign-editor-types';
import { buildCreatorPayloadItem } from '../../../hooks';

type BuildCampaignUpdatePayloadArgs = {
  campaign: Campaign;
  creators: CampaignTrackHandle[];
  payload: CampaignEditorSubmitPayload;
  posts: CampaignTrackPost[];
  workspaceTimezone?: string;
};

function buildNextCreatorItems(payload: CampaignEditorSubmitPayload): CampaignTrackHandle[] {
  return payload.creatorItems.flatMap((item) => {
    const classification = classifyCreatorSource(item.sourceValue);
    if (!classification) return [];

    const nextCreator = buildCreatorPayloadItem(item, classification);
    return nextCreator ? [nextCreator] : [];
  });
}

function buildNextManualPostItems(payload: CampaignEditorSubmitPayload) {
  const seen = new Set<string>();

  return payload.postItems.flatMap((item) => {
    const nextPostItem = buildCampaignTrackPostItem(item.postUrl);
    if (!nextPostItem) return [];
    if (!isCampaignSupportedPlatform(nextPostItem.platform)) return [];

    const dedupeKey = nextPostItem.post_url.toLowerCase();
    if (seen.has(dedupeKey)) return [];

    seen.add(dedupeKey);
    return [{ ...nextPostItem, platform: nextPostItem.platform }];
  });
}

export function buildCampaignUpdatePayload({
  campaign,
  payload,
  workspaceTimezone,
}: BuildCampaignUpdatePayloadArgs) {
  const trackingTerms = splitTrackingTerms(payload.creatorKeywords);
  const nextCreatorItems = buildNextCreatorItems(payload);
  const nextPostItems = buildNextManualPostItems(payload);
  const startDateUpdate = resolveCampaignStartDateUpdate({
    campaign,
    nextStartDate: payload.campaignStartDate,
    workspaceTimezone,
  });

  const request = {
    start_date: startDateUpdate,
    end_date: payload.hasEndDate && payload.campaignEndDate ? payload.campaignEndDate : null,
    keywords: trackingTerms.keywords,
    hashtags: trackingTerms.hashtags,
    mentions: trackingTerms.mentions,
    items: [...nextCreatorItems, ...nextPostItems].map((item) => ({
      ...item,
      platform: toCampaignPlatform(item.platform),
    })),
  } satisfies UpdateCampaignRequest;

  return request;
}
